import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { X, Sparkles, Zap, Minus } from 'lucide-react';

interface UpgradePromptProps {
  onClose: () => void;
  onUpgrade: () => void;
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({ onClose, onUpgrade }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    // Fade in after component mounts
    const timer = setTimeout(() => setIsVisible(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for fade out animation
  };

  const handleMinimize = () => {
    setIsMinimized(true);
    // Auto-expand after 2 minutes
    setTimeout(() => {
      if (isMinimized) {
        setIsMinimized(false);
      }
    }, 120000); // 2 Minuten
  };

  const handleLaterClick = () => {
    // "Später" bedeutet: Schließe für längere Zeit (1 Stunde)
    localStorage.setItem('upgradePromptSnoozeUntil', (Date.now() + 60 * 60 * 1000).toString());
    handleClose();
  };

  return (
    <div className={`fixed bottom-4 right-4 z-40 transition-all duration-300 ${
      isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
    }`}>
      <Card className={`bg-white border border-gray-200/60 shadow-lg hover:shadow-xl transition-all duration-300 ${
        isMinimized ? 'w-16 h-16' : 'w-80'
      }`}>
        {isMinimized ? (
          // Minimized state - just an icon
          <div 
            className="w-full h-full flex items-center justify-center cursor-pointer hover:bg-emerald-50 rounded-lg transition-colors group"
            onClick={() => setIsMinimized(false)}
            title="Upgrade-Angebot anzeigen"
          >
            <div className="w-10 h-10 bg-emerald-600 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
          </div>
        ) : (
          // Expanded state
          <>
            <CardHeader className="pb-3 pt-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-emerald-600 rounded-xl flex items-center justify-center">
                    <Sparkles className="w-4 h-4 text-white" />
                  </div>
                  <CardTitle className="text-base font-medium text-gray-900">
                    Bereit für mehr?
                  </CardTitle>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={handleMinimize}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
                    title="Minimieren (zeigt sich in 2 Min. wieder)"
                  >
                    <Minus className="w-3 h-3" />
                  </button>
                  <button
                    onClick={handleClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors p-1.5 rounded-lg hover:bg-gray-100"
                    title="Schließen"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
              <Badge className="bg-emerald-100 text-emerald-800 text-xs w-fit hover:bg-emerald-100 hover:text-emerald-800 rounded-lg">
                Premium für nur 16,90€/Jahr
              </Badge>
            </CardHeader>
        
            <CardContent className="pt-0 pb-4">
              <div className="space-y-4">
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Zap className="w-3 h-3 text-emerald-600" />
                  </div>
                  <span className="text-gray-700">
                    <strong>Unbegrenzte Produkte</strong> statt nur einem
                  </span>
                </div>
          
                <div className="bg-emerald-50/50 p-3 rounded-xl border border-emerald-200/60">
                  <p className="text-emerald-800 text-xs text-center leading-relaxed">
                    💡 <strong>Schon beim ersten vermiedenen 5€-Kauf</strong> hast du Premium 3 Monate bezahlt!
                  </p>
                </div>
          
                <div className="flex gap-3">
                  <Button
                    onClick={handleLaterClick}
                    variant="outline"
                    size="sm"
                    className="flex-1 border-gray-200 text-gray-600 hover:bg-gray-50 hover:text-gray-700 h-8 text-xs rounded-lg"
                  >
                    Später
                  </Button>
                  <Button
                    onClick={onUpgrade}
                    size="sm"
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs rounded-lg"
                  >
                    Upgrade
                  </Button>
                </div>
          
                <p className="text-xs text-gray-500 text-center">
                  Kein Abonnement • 14 Tage Widerrufsrecht
                </p>
              </div>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
}; 